/* GENERATED from src/js/ndog-tiled-slider.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-tiled-slider.js
  Self-contained web component for the NDOG Tiled Slider section.
  Drives a CSS scroll-snap track via tiles, arrows, dots + optional autoplay.
  No external dependencies, no global side effects.
*/
// Swipe-hint nudge config. The hint repeats while the slider is in view and the user
// hasn't slid the cards yet; once they slide, it's dismissed for the rest of the session.
const NDOG_NUDGE_KEY = 'ndog-tiled-slider-slid'; // localStorage: timestamp of the last dismissal
const NDOG_NUDGE_DISTANCE = 24;   // px the track travels during a hint (bigger = harder to miss)
const NDOG_NUDGE_KICKOFF = 1000;  // ms before the first hint after the slider appears
const NDOG_NUDGE_INTERVAL = 6000; // ms between repeated hints
const NDOG_NUDGE_TTL = 7 * 24 * 60 * 60 * 1000; // re-show the hint if the user last slid >7 days ago

// True while the visitor slid the cards recently enough that we should stay quiet (TTL window).
function ndogNudgeRecentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(NDOG_NUDGE_KEY));
    return ts > 0 && Date.now() - ts < NDOG_NUDGE_TTL;
  } catch (e) {
    return false; // storage blocked → err on the side of showing the hint
  }
}

// Remember when the visitor slid the cards, so the hint stays off for the next TTL window.
function ndogNudgeRecordDismissed() {
  try { localStorage.setItem(NDOG_NUDGE_KEY, String(Date.now())); } catch (e) {}
}

if (!customElements.get('ndog-tiled-slider')) {
  customElements.define(
    'ndog-tiled-slider',
    class NdogTiledSlider extends HTMLElement {
      // Cache elements, bail out early if the slider is empty.
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        this.slides = Array.from(this.querySelectorAll('.ndog-slide'));
        this.tiles = Array.from(this.querySelectorAll('.ndog-tile[data-slide-key]'));
        this.dots = Array.from(this.querySelectorAll('[data-dot-index]'));
        this.arrows = Array.from(this.querySelectorAll('[data-dir]'));
        if (!this.track || this.slides.length === 0) return;

        this.activeIndex = 0;
        // While true, the scroll observer is ignored so a programmatic glide lights
        // up only its target tile — not every slide the track passes on the way.
        this.isNavigating = false;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        this.hideControlsIfSingle();
        this.bindEvents();
        this.observeSlides();
        this.setInitialActive();
        this.setupAutoplay();
        this.setupNudge();
      }

      // Clean up timers, observers and listeners so nothing leaks.
      disconnectedCallback() {
        this.stopAutoplay();
        this.stopNudgeLoop();
        clearTimeout(this.settleTimer);
        if (this.observer) this.observer.disconnect();
        if (this.nudgeObserver) this.nudgeObserver.disconnect();
        if (this.nudgeCleanup) this.nudgeCleanup(false);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onTrackScroll) this.track.removeEventListener('scroll', this.onTrackScroll);
      }

      // With a single slide, navigation controls are meaningless — hide them.
      hideControlsIfSingle() {
        if (this.slides.length > 1) return;
        this.querySelectorAll('.ndog-tiled-slider__arrows, .ndog-tiled-slider__dots')
          .forEach((el) => { el.style.display = 'none'; });
      }

      // Wire tiles, arrows, dots and autoplay-pausing interactions.
      bindEvents() {
        this.tiles.forEach((tile) => {
          tile.addEventListener('click', () => this.goToKey(tile.dataset.slideKey));
        });
        this.arrows.forEach((arrow) => {
          arrow.addEventListener('click', () => {
            arrow.dataset.dir === 'next' ? this.next() : this.prev();
          });
        });
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goToIndex(Number(dot.dataset.dotIndex)));
        });
        ['pointerenter', 'focusin', 'touchstart'].forEach((evt) => {
          this.addEventListener(evt, () => this.stopAutoplay(), { passive: true });
        });
        ['pointerleave', 'focusout'].forEach((evt) => {
          this.addEventListener(evt, () => this.startAutoplay());
        });
        // Re-enable the observer once a programmatic glide stops moving. Debounced on
        // scroll events, so it adapts to any glide distance instead of a fixed guess.
        this.onTrackScroll = () => {
          if (!this.isNavigating) return;
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => { this.isNavigating = false; }, 120);
        };
        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
      }

      // Scroll the track so the slide at index becomes the active/snapped one.
      goToIndex(index) {
        if (index < 0) index = this.slides.length - 1;
        if (index >= this.slides.length) index = 0;
        const slide = this.slides[index];
        if (!slide) return;
        // Subtract the track's scroll-padding so the slide lands on its inset snap
        // point (mobile peek) instead of flush against the edge.
        const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
        const left = slide.getBoundingClientRect().left
          - this.track.getBoundingClientRect().left + this.track.scrollLeft - pad;
        // Ignore the scroll observer until the glide settles, so only the target tile
        // gets the active class — not every slide the track passes along the way.
        this.isNavigating = true;
        this.track.scrollTo({ left, behavior: this.reduceMotion ? 'auto' : 'smooth' });
        this.setActive(index);
        this.scheduleSettle();
      }

      // Pick the starting slide from the URL hash (matching a slide key) or default to
      // the first one, then show it immediately — fixes "no tile active after reload".
      setInitialActive() {
        let index = 0;
        const hashKey = decodeURIComponent((window.location.hash || '').replace(/^#/, '')).trim();
        if (hashKey) {
          const found = this.slides.findIndex((s) => (s.dataset.slideKey || '') === hashKey);
          if (found !== -1) index = found;
        }
        // Pre-set so setActive() won't treat the initial pick as a user-driven slide.
        this.activeIndex = index;
        if (index > 0) this.isNavigating = true; // ignore the observer's first callback
        this.setActive(index);
        if (index === 0) return;
        // Hash deep-link to a later slide: jump straight there (no glide → no flashing).
        requestAnimationFrame(() => {
          const slide = this.slides[index];
          if (!slide) { this.isNavigating = false; return; }
          const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
          this.track.scrollLeft = slide.offsetLeft - pad;
          this.scheduleSettle();
        });
      }

      // Clear the navigating guard once the glide stops. The 120ms debounce in
      // onTrackScroll handles the normal case; this is the hard fallback for when no
      // scroll events fire (e.g. the target slide is already in view).
      scheduleSettle() {
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => { this.isNavigating = false; }, 700);
      }

      next() { this.goToIndex(this.activeIndex + 1); }
      prev() { this.goToIndex(this.activeIndex - 1); }

      // Find the slide matching a tile's key and scroll to it.
      goToKey(key) {
        if (!key) return;
        const index = this.slides.findIndex((s) => (s.dataset.slideKey || '') === key);
        if (index !== -1) this.goToIndex(index);
      }

      // Reflect the active slide on dots and tiles.
      setActive(index) {
        // Moving off the first card means the user found the swipe — stop hinting for good.
        if (index !== this.activeIndex && index !== 0) this.markSlid();
        this.activeIndex = index;
        const key = this.slides[index] ? this.slides[index].dataset.slideKey : '';
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
        this.tiles.forEach((t) => t.classList.toggle('is-active', (t.dataset.slideKey || '') === key));
      }

      // Keep the active state in sync while the user swipes/scrolls the track.
      observeSlides() {
        this.observer = new IntersectionObserver(
          (entries) => {
            // During a programmatic glide the target tile is set explicitly; don't let
            // slides crossing 60% visibility flash the active class on the way past.
            if (this.isNavigating) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const index = this.slides.indexOf(entry.target);
                if (index !== -1) this.setActive(index);
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((s) => this.observer.observe(s));
      }

      // Decide whether autoplay should run for the current breakpoint.
      setupAutoplay() {
        if (this.reduceMotion || this.slides.length <= 1) return;
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const enabled = isDesktop
          ? this.dataset.autoplayDesktop === 'true'
          : this.dataset.autoplayMobile === 'true';
        if (!enabled) return;
        this.autoplayMs = (Number(this.dataset.autoplaySpeed) || 5) * 1000;
        this.onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
        document.addEventListener('visibilitychange', this.onVisibility);
        this.startAutoplay();
      }

      startAutoplay() {
        if (!this.autoplayMs || this.timer) return;
        this.timer = setInterval(() => this.next(), this.autoplayMs);
      }

      stopAutoplay() {
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
      }

      // Mobile only: hint that the cards are swipeable, repeatedly, until the user slides them.
      setupNudge() {
        if (this.reduceMotion || this.slides.length <= 1) return;
        if (!window.matchMedia('(max-width: 989px)').matches) return;
        if (ndogNudgeRecentlyDismissed()) return;

        this.hasSlid = false;
        this.isTrackVisible = false;

        // Run the hint loop only while the slider is on screen.
        this.nudgeObserver = new IntersectionObserver(
          (entries) => {
            this.isTrackVisible = entries.some((entry) => entry.isIntersecting);
            if (this.isTrackVisible && !this.hasSlid) this.startNudgeLoop();
            else this.stopNudgeLoop();
          },
          { threshold: 0.3 }
        );
        this.nudgeObserver.observe(this.track);

        // While the user is touching the slider, hold the hint; resume on release if not slid yet.
        this.onNudgeDown = () => { this.abortNudge(); this.stopNudgeLoop(); };
        this.onNudgeUp = () => {
          if (!this.hasSlid && this.isTrackVisible) this.startNudgeLoop();
        };
        this.addEventListener('pointerdown', this.onNudgeDown, { passive: true });
        this.addEventListener('pointerup', this.onNudgeUp, { passive: true });
        this.addEventListener('pointercancel', this.onNudgeUp, { passive: true });
      }

      // Start the recurring hint: one shortly after appearing, then every NDOG_NUDGE_INTERVAL ms.
      startNudgeLoop() {
        if (this.nudgeTimer || this.hasSlid) return;
        this.nudgeKickoff = setTimeout(() => this.playNudge(), NDOG_NUDGE_KICKOFF);
        this.nudgeTimer = setInterval(() => this.playNudge(), NDOG_NUDGE_INTERVAL);
      }

      // Pause the recurring hint (slider off screen, user touching, or dismissed).
      stopNudgeLoop() {
        if (this.nudgeKickoff) { clearTimeout(this.nudgeKickoff); this.nudgeKickoff = null; }
        if (this.nudgeTimer) { clearInterval(this.nudgeTimer); this.nudgeTimer = null; }
      }

      // Dismiss the hint for the rest of the session once the user has slid the cards.
      markSlid() {
        if (this.hasSlid) return;
        this.hasSlid = true;
        this.abortNudge();
        this.stopNudgeLoop();
        if (this.nudgeObserver) { this.nudgeObserver.disconnect(); this.nudgeObserver = null; }
        ndogNudgeRecordDismissed();
      }

      // Stop any in-flight hint animation immediately (cleanup restores snapping).
      abortNudge() {
        if (this.nudgeCleanup) this.nudgeAborted = true;
      }

      // Tween the track to the left and softly settle back — never re-snaps to a different card.
      playNudge() {
        if (this.nudgeRaf || this.hasSlid) return; // don't stack hints
        const distance = NDOG_NUDGE_DISTANCE;
        const prevSnap = this.track.style.scrollSnapType;
        this.track.style.scrollSnapType = 'none'; // stop mandatory snap fighting the tween
        this.nudgeAborted = false;

        this.nudgeCleanup = (resetScroll) => {
          if (this.nudgeRaf) { cancelAnimationFrame(this.nudgeRaf); this.nudgeRaf = null; }
          this.track.style.scrollSnapType = prevSnap || '';
          if (resetScroll) this.track.scrollLeft = 0;
          this.nudgeCleanup = null;
        };

        const easeOut = (t) => 1 - Math.pow(1 - t, 3);
        const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

        // Run one rAF tween of scrollLeft, then either chain the next phase or clean up.
        const tween = (from, to, duration, ease, done) => {
          const start = performance.now();
          const frame = (now) => {
            if (this.nudgeAborted) { this.nudgeCleanup(false); return; }
            const p = Math.min((now - start) / duration, 1);
            this.track.scrollLeft = from + (to - from) * ease(p);
            if (p < 1) this.nudgeRaf = requestAnimationFrame(frame);
            else if (done) done();
            else this.nudgeCleanup(true);
          };
          this.nudgeRaf = requestAnimationFrame(frame);
        };

        tween(0, distance, 240, easeOut, () => tween(distance, 0, 460, easeInOut));
      }
    }
  );
}
